<!-- **** --------------------------------------------------------------------------
	SHARED SCRIPT
-------------------------------------------------------------------------- **** -->
<script lang="ts">
    import vm, { JourneyLines, JourneyNode, JourneyNodes, JourneyTextNode } from '@journeys/Journeys.viewmodel';
    import { reactive } from 'vue';

    import { NodeType } from '@journeys/Enums';

    // import { instanceWasm, _gom } from '@shared/modules/tiny'

    // directives
    // import { vObserver } from '@shared/directives'

    // utils
    import { calc_snap, cancelFrame, requestFrame, thresholdCallListener, uid } from '@utils';

    // directives

    // components
    import { NodeFrame } from '../NodePartials/NodeAssets';
    import Node from './JourneyNode.vue';
    import TextNode from './JourneyTextNode.vue';

    // assets

    // this is mostly for debugging, otherwise components are Anonymous
    const component = {
        name: 'CanvasLayer_Nodes',
    };
    export default {
        name: component.name,
    };
</script>

<!-- **** --------------------------------------------------------------------------
	SCRIPT
-------------------------------------------------------------------------- **** -->
<script lang="ts" setup>
    const cid = uid();

    const props = withDefaults(
        defineProps<{
            data: JourneyNodes;
            lineList: JourneyLines;
        }>(),
        {}
    );

    const { settings, editor, canvas } = vm,
        { snap_m } = settings.grid,
        { p_mod } = settings.canvas,
        /** local state */
        state = reactive({
            busy: false,
        }),
        doubleClick = thresholdCallListener(settings.canvas.doubleClickThreshold);

    let frameReq: number = 0;

    let cache = {
        showPropertyWindow: editor.state.showPropertyWindow,
    };

    const showNodeMenu = (show: boolean = true) => {
        if (show === editor.state.showNodeMenu) return;
        else if (show) cache.showPropertyWindow = editor.state.showPropertyWindow;

        editor.state.showNodeMenu = show;

        // show !== editor.state.showPropertyWindow &&
        // editor.events.emit('showpropertywindow', show || cache.showPropertyWindow)
    };

    const startResize = (ev: PointerEvent, node: JourneyNode) => {
        editor.state.busy = true;

        let asp = node.f === NodeFrame.RoundedRect ? 5 : 1,
            evt = ev.target as HTMLElement,
            /** old value */
            o = {
                x: ev.x,
                y: ev.y,
                nx: node.x,
                ny: node.y,
                h: node.h,
                w: node.w,
            };

        const handleResize = (ev: PointerEvent) => {
            cancelFrame(frameReq);
            frameReq = requestFrame(() => {
                node.w = o.w + ev.y - o.y;
                node.h = node.w / asp;
                node.x = o.nx - (ev.y - o.y) / 2;
                node.y = o.ny - (node.h - o.h) / 2;
            });
        };

        document.addEventListener('pointermove', handleResize);
        document.addEventListener(
            'pointerup',
            () => {
                editor.state.busy = false;

                cancelFrame(frameReq);
                requestFrame(() => {
                    node.w = ~~node.w;
                    node.h = ~~node.h;
                    node.x = ~~node.x;
                    node.y = ~~node.y;
                });
                document.removeEventListener('pointermove', handleResize);
            },
            { once: true }
        );
    };

    const startConnect = (ev: PointerEvent, direction: string, node: JourneyNode) => {
        editor.events.emit('drawline', ev, { direction, node });
    };

    const updateLineHitboxes = () => {
        let updated: any = {};

        for (let lid in editor.tools.sel_lines) {
            if (!updated[lid]) {
                updated[lid] = true;

                let line = props.lineList[lid];

                editor.events.emit('newlinehitboxes', line);
            }
        }

        for (let nid in editor.tools.sel_nodes) {
            let node = props.data[nid] as JourneyNode;

            for (let l in node.lnto)
                if (!updated[l]) {
                    updated[l] = true;
                    editor.events.emit('newlinehitboxes', props.lineList[l]);
                }
            for (let l in node.lnfr)
                if (!updated[l]) {
                    updated[l] = true;
                    editor.events.emit('newlinehitboxes', props.lineList[l]);
                }
        }

        for (let pid in editor.tools.sel_points) {
            let lid = editor.tools.sel_points[pid].id;

            if (!updated[lid]) {
                updated[lid] = true;
                editor.events.emit('newlinehitboxes', props.lineList[lid]);
            }
        }
    };

    const handleMoveNode = (ev: PointerEvent, node: JourneyNode) => {
        if (
            /(line)/.test(editor.state.activeTool) ||
            (/(nav)/.test(editor.state.activeTool) && !ev.altKey) ||
            (/(pointer)/.test(editor.state.activeTool) && ev.altKey)
        )
            return;

        let isSelected = editor.tools.sel_nodes[node.id],
            modSelect = true,
            moveStart_ts = Date.now(),
            nodes: Array<any> = Object.values(editor.tools.sel_nodes),
            nodes_len = nodes.length,
            points: Array<any> = Object.values(editor.tools.sel_points),
            point_len: number = points.length,
            lines: Array<any> = Object.values(editor.tools.sel_lines),
            line_len: number = points.length,
            moveSelection = nodes_len + point_len + line_len > 1 ? true : false,
            snap = settings.grid.snap,
            snap_spacing = settings.grid.spacing,
            o: any = {
                x: ev.x,
                y: ev.y,
                nx: node.x,
                ny: node.y,
                sel_area: {
                    x: editor.tools.sel_dims.l,
                    y: editor.tools.sel_dims.t,
                },
            };

        if (moveSelection) {
            for (let sn of nodes) {
                sn.runtime ||= {};
                sn.runtime.nx = sn.x;
                sn.runtime.ny = sn.y;
            }

            for (let pt of points) {
                if (typeof pt.idx === 'number') {
                    pt.x = props.lineList[pt.id].points[pt.idx].x;
                    pt.y = props.lineList[pt.id].points[pt.idx].y;
                } else if (pt.idx === 'to') {
                    pt.ex = props.lineList[pt.id].ex;
                    pt.ey = props.lineList[pt.id].ey;
                } else if (pt.idx === 'fr') {
                    pt.x = props.lineList[pt.id].x;
                    pt.y = props.lineList[pt.id].y;
                }
            }
        }

        editor.state.busy = true;
        canvas.el?.setPointerCapture(ev.pointerId);

        const handleMove = (ev: PointerEvent) => {
            if (modSelect && Math.abs(o.x - ev.x) + Math.abs(o.y - ev.y) > 30) {
                editor.state.moving = true;
                modSelect = false;
            }

            cancelFrame(frameReq);
            frameReq = requestFrame(() => {
                let n_x = ~~((ev.x - o.x) * p_mod.value),
                    n_y = ~~((ev.y - o.y) * p_mod.value);

                if (isSelected && moveSelection) {
                    let { x, y } = snap ? calc_snap({ x: n_x, y: n_y }, snap_spacing) : { x: n_x, y: n_y };

                    if (x === editor.dev.props.sel_x && y === editor.dev.props.sel_y) return;

                    editor.dev.props.sel_x = x;
                    editor.dev.props.sel_y = y;

                    for (let nd of nodes) {
                        for (let to in nd.lnto) {
                            if (editor.tools.sel_lines[to]) continue;

                            props.lineList[to].runtime.toVertexMod.x = x;
                            props.lineList[to].runtime.toVertexMod.y = y;
                        }
                        for (let fr in nd.lnfr) {
                            if (editor.tools.sel_lines[fr]) continue;

                            props.lineList[fr].runtime.frVertexMod.x = x;
                            props.lineList[fr].runtime.frVertexMod.y = y;
                        }
                    }

                    for (let pt of points) {
                        if (editor.tools.sel_lines[pt.id]) continue;

                        if (typeof pt.idx === 'number') {
                            props.lineList[pt.id].points[pt.idx].x = pt.x + x;
                            props.lineList[pt.id].points[pt.idx].y = pt.y + y;
                        } else if (pt.idx === 'to') {
                            props.lineList[pt.id].ex = pt.ex + x;
                            props.lineList[pt.id].ey = pt.ey + y;
                        } else if (pt.idx === 'fr') {
                            props.lineList[pt.id].x = pt.x + n_x;
                            props.lineList[pt.id].y = pt.y + n_y;
                        }
                    }
                } else {
                    let { x, y } = snap
                        ? calc_snap({ x: o.nx + n_x, y: o.ny + n_y }, snap_spacing, { h: node.h, w: node.w })
                        : { x: o.nx + n_x, y: o.ny + n_y };

                    if (x === node.x && y === node.y) return;

                    node.x = x;
                    node.y = y;
                }
            });
        };

        const handleRelease = (ev: PointerEvent): void => {
            editor.state.busy = false;
            editor.state.moving = false;

            cancelFrame(frameReq);

            if (moveSelection) {
                for (let sn of nodes) {
                    sn.x += editor.dev.props.sel_x;
                    sn.y += editor.dev.props.sel_y;

                    for (let to in sn.lnto) {
                        if (editor.tools.sel_lines[to]) continue;

                        props.lineList[to].runtime.toVertexMod.x = 0;
                        props.lineList[to].runtime.toVertexMod.y = 0;
                    }
                    for (let fr in sn.lnfr) {
                        if (editor.tools.sel_lines[fr]) continue;

                        props.lineList[fr].runtime.frVertexMod.x = 0;
                        props.lineList[fr].runtime.frVertexMod.y = 0;
                    }
                }

                for (let ln of lines) {
                    for (let i = 0; i < ln.points.length; i++) {
                        let pt = ln.points[i];

                        props.lineList[ln.id].points[i].x = pt.x + editor.dev.props.sel_x;
                        props.lineList[ln.id].points[i].y = pt.y + editor.dev.props.sel_y;
                    }
                    let line = props.lineList[ln.id];

                    line.x += editor.dev.props.sel_x;
                    line.y += editor.dev.props.sel_y;
                    line.ex += editor.dev.props.sel_x;
                    line.ey += editor.dev.props.sel_y;
                }

                for (let pt of points) {
                    pt.x += editor.dev.props.sel_x;
                    pt.y += editor.dev.props.sel_y;
                }

                editor.dev.props.sel_x = 0;
                editor.dev.props.sel_y = 0;
            }

            if (Date.now() - moveStart_ts < settings.canvas.selectSensitivity && modSelect) {
                let doubleClicked = doubleClick();
                if (!editor.state.showPropertyWindow && doubleClicked) {
                    editor.events.emit('showpropertywindow', true);
                    editor.state.showNodeMenu = true;
                }

                if (ev.shiftKey) {
                    if (isSelected) {
                        delete editor.tools.sel_nodes[node.id];

                        for (let lid in node.lnto) delete editor.tools.sel_lines[lid];
                        for (let lid in node.lnfr) delete editor.tools.sel_lines[lid];

                        if (node.id === editor.tools.sel_keyObj) editor.tools.sel_keyObj = null;
                    } else {
                        editor.tools.sel_nodes[node.id] = node;

                        for (let lid in node.lnto) {
                            let to = props.lineList[lid].to;

                            if (to && editor.tools.sel_nodes[to]) editor.tools.sel_lines[lid] = props.lineList[lid];
                        }

                        for (let lid in node.lnfr) {
                            let fr = props.lineList[lid].fr;

                            if (fr && editor.tools.sel_nodes[fr]) editor.tools.sel_lines[lid] = props.lineList[lid];
                        }
                    }

                    nodes_len - 1 || showNodeMenu(!isSelected);
                } else {
                    if (!isSelected) {
                        editor.tools.sel_nodes = {};
                        editor.tools.sel_points = {};
                        editor.tools.sel_lines = {};
                    }

                    if (editor.tools.sel_count > 1 && node.id === editor.tools.sel_keyObj) editor.tools.sel_keyObj = null;
                    else editor.tools.sel_keyObj = node.id;

                    editor.tools.sel_nodes[node.id] = node;

                    !doubleClicked && showNodeMenu(!!editor.tools.sel_keyObj);
                }
            }

            updateLineHitboxes();

            for (let l in node.lnto) editor.events.emit('newlinehitboxes', props.lineList[l]);
            for (let l in node.lnfr) editor.events.emit('newlinehitboxes', props.lineList[l]);

            editor.calcNodeArea();

            if (isSelected) {
                let n_x = ~~((ev.x - o.x) * p_mod.value),
                    n_y = ~~((ev.y - o.y) * p_mod.value);

                editor.tools.sel_dims.t = o.sel_area.y + n_y;
                editor.tools.sel_dims.b = o.sel_area.y + editor.tools.sel_dims.h + n_y;
                editor.tools.sel_dims.r = o.sel_area.x + editor.tools.sel_dims.w + n_x;
                editor.tools.sel_dims.l = o.sel_area.x + n_x;
            }

            editor.events.emit('calcselarea', {
                nodes: editor.tools.sel_nodes,
                points: editor.tools.sel_points,
                lines: editor.tools.sel_lines,
            });

            document.removeEventListener('pointermove', handleMove);
            // TODO: document.removeEventListener('wheel', handleMove)
        };

        document.addEventListener('pointermove', handleMove);
        document.addEventListener('pointerup', handleRelease, { once: true });
    };

    // `transform: translate3d(${ node.x }px, ${ node.y }px, 0)`,
    // `transform: translate3d(${ node.x + state.n_x }px, ${ node.y + state.n_y }px, 0)`
</script>

<!-- **** --------------------------------------------------------------------------
	TEMPLATE
-------------------------------------------------------------------------- **** -->
<template>
    <template v-for="(node, index) in data" :key="`jn${node.id}`">
        <TextNode
            v-if="node.data.type === NodeType.Text"
            :node="node as JourneyTextNode"
            :moveNode="handleMoveNode"
            :style="`opacity: ${editor.tools.sel_count > 1 && editor.tools.sel_nodes[node.id] ? 0 : 1}`"
            :class="[editor.tools.sel_nodes[node.id] && 'selected', state.busy && 'component-busy']" />
        <Node
            v-else
            :node="node as JourneyNode"
            :moveNode="handleMoveNode"
            :startConnect="startConnect"
            :startResize="startResize"
            :style="`opacity: ${editor.tools.sel_count > 1 && editor.tools.sel_nodes[node.id] ? 0 : 1}`"
            :class="[editor.tools.sel_nodes[node.id] && 'selected', state.busy && 'component-busy']" />
    </template>
</template>

<!-- **** --------------------------------------------------------------------------
	STYLES
-------------------------------------------------------------------------- **** -->
<style>
    /* .node.selected,
.node.selected {
   transform: scale(1.1);
} */

    .editor-busy .node,
    .editor-busy .text-node,
    .editor-busy .node-button,
    .editor-busy .node-frame.pe-visible {
        pointer-events: none !important;
    }

    .node-text-container {
        display: flex;
        position: absolute;
        justify-content: center;
        text-align: center;
        align-items: center;
        width: 82%;
        top: 50%;
        right: 0;
        left: 18%;
        transform: translate3d(0, -50%, 0);
        opacity: 0.8;
        pointer-events: none;
    }
    .node-text-container h4 {
        padding: 0.1rem;
        color: rgb(224, 222, 222);
        font-size: 0.45rem;
        line-height: 0.6rem;
        letter-spacing: 0;
        height: 100%;
    }

    .initial-marker {
        position: absolute;
        height: 1.2rem;
        width: 1.6rem;
        bottom: calc(100% + 0.4rem);
        left: 50%;
        transform: translate3d(-50%, 0, 0);
        background-color: rgb(17, 0, 255);
        border-radius: 0.5rem;
        overflow: hidden;
        outline: 0.2rem solid rgb(255, 255, 255);
    }
    .initial-marker svg {
        height: 1.2rem;
        width: 1.6rem;
    }
    .initial-marker .node-icon {
        fill: #fff;
    }
    .initial-marker .node-icon-shadow {
        fill: rgba(66, 52, 52, 0.144);
    }

    .fade-enter-active,
    .fade-leave-active,
    .fade-enter-active .canvas-node,
    .fade-leave-active .canvas-node {
        transition-property: opacity, transform;
        transition-duration: 0.6s;
        transition-timing-function: ease;
    }

    .fade-leave-active,
    .fade-leave-active .canvas-node {
        transition-duration: 0.2s;
    }

    /* .fade-enter-from, */
    .fade-leave-to {
        opacity: 0;
    }
    /* .fade-enter-from .canvas-node,
.fade-leave-to .canvas-node {
   transform: scale(1.05) rotate(13deg);
} */

    .node {
        display: block;
        position: absolute;
        top: 0;
        left: 0;
        /* box-shadow: .2rem .7rem .7rem -.4rem rgba(63, 47, 47, 0.2); */

        /* transition: transform var(--td-fast) ease-out; */
        will-change: transform, height, width;

        pointer-events: auto;
        z-index: 1;
    }
    /* .canvas-node, */
    .node svg {
        pointer-events: none;
    }

    .node-title-wrapper {
        position: absolute;
        width: 300%;

        bottom: calc(100% + 0.4rem);
        transform-origin: bottom;
        transition: transform 0.1s linear;
        pointer-events: none;
        text-align: center;
        left: -100%;
    }

    .node-title {
        display: inline-block;
        font-size: 0.9rem;
        text-align: center;
        white-space: normal;
        word-wrap: normal;
        background-color: #f0eeee85;
        margin: 0 0 0.313rem;
    }

    .node-ui {
        position: absolute;
        height: 100%;
        width: 100%;
        top: 0;
        opacity: 0;
        transition-property: opacity;
        transition-duration: 0.14s;
        transition-delay: 0.1s;
        transition-timing-function: ease;
        pointer-events: none;
    }

    .node:hover .node-ui,
    .node.selected .node-ui {
        opacity: 1;
        transition-delay: 0s;
    }

    .node-button {
        display: flex;
        position: absolute;
        align-items: center;
        justify-content: center;
        height: 1rem;
        width: 1rem;
        pointer-events: all;
        background-color: #6633ff;
        border-radius: 50%;
        /* box-shadow: 2px 2px 6px -1px #a99e9e; */
    }

    .node-button.center-left {
        right: calc(100% + 0.4rem);
        top: 0;
        bottom: 0;
        margin: auto;
        transform-origin: right;
    }

    .node-button.center-right {
        left: calc(100% + 0.4rem);
        top: 0;
        bottom: 0;
        margin: auto;
        transform-origin: left;
    }
    .node-button.bottom-right {
        height: 1rem;
        width: 1rem;
        left: calc(100% + 0.2rem);
        top: calc(100% + 0.2rem);
        transform-origin: top left;
    }

    .node-button.bottom-right svg {
        height: 55%;
        width: 55%;
    }

    .node-button svg {
        height: 50%;
        width: 50%;
        fill: #ffffff;
    }

    /** nice thing that is fighting with me */
    /* .canvas-node .pe-visible {
   pointer-events: visible;
} */
</style>
